import type { SurveyConfig } from "@expert/app-config/types";
import type { CallbackState } from "@/sdk";
import type { PartnerProgram, SummaryResolution } from "@/shared-types";

export const selectProgramName = (
    useCustomerAgreementProgramName: boolean,
    wasClaimFiled: boolean,
    partnerConfigProgramName: string | undefined,
    customerAgreementProgramName: string | undefined,
    ignoreProgram: boolean,
    excludedPrograms: string[],
): string | undefined => {
    const programName = useCustomerAgreementProgramName ? customerAgreementProgramName : partnerConfigProgramName;
    const isExcludedProgram = excludedPrograms.some(
        (excludedProgram) => excludedProgram.toLowerCase() === programName?.toLowerCase(),
    );

    if (!programName || isExcludedProgram) {
        return ignoreProgram ? "NA" : undefined;
    }
    return wasClaimFiled ? `${programName}-retail` : programName;
};

export const selectSurveyId = (
    useCustomerAgreementProgramName: boolean,
    wasClaimFiled: boolean,
    customerProgram: PartnerProgram | undefined,
    surveyConfig: SurveyConfig | undefined,
    resolutionStatus?: SummaryResolution,
): string | undefined => {
    const config = useCustomerAgreementProgramName ? surveyConfig : customerProgram?.survey;

    if (
        resolutionStatus === "not resolved" &&
        surveyConfig?.shouldSurveyNotResolved &&
        surveyConfig?.unresolvedSurveyId
    ) {
        return surveyConfig.unresolvedSurveyId;
    }

    return wasClaimFiled ? config?.claimsSurveyId : config?.generalSurveyId;
};

/* Conditions for transfer to survey:
    1. Does not have a callback scheduled
    2. Does not have an active conference
    3. Customer has not already left after conference
    4. Resolution status is eligible for survey (controlled by feature flags)
*/
export const shouldTransferToSurvey = ({
    customerLeftTheConference,
    callbackState,
    isConference,
    conferencePanelOpen,
    resolutionStatus,
    surveyableResolutionStatuses,
}: {
    customerLeftTheConference: boolean;
    callbackState: CallbackState | null;
    isConference: boolean;
    conferencePanelOpen: boolean;
    resolutionStatus: SummaryResolution;
    surveyableResolutionStatuses: SummaryResolution[];
}): boolean => {
    const hasCallback = !!callbackState;
    const activeConference = isConference && conferencePanelOpen;

    const isResolutionSurveyable = surveyableResolutionStatuses.includes(resolutionStatus);

    return !(hasCallback || activeConference || customerLeftTheConference) && isResolutionSurveyable;
};

export const shouldSendSurveyData = ({
    customerLeftTheConference,
    callbackState,
    isConference,
    conferencePanelOpen,
}: {
    customerLeftTheConference: boolean;
    callbackState: CallbackState | null;
    isConference: boolean;
    conferencePanelOpen: boolean;
}): boolean => {
    const hasCallback = !!callbackState;
    const activeConference = isConference && conferencePanelOpen;

    return !hasCallback && !activeConference && !customerLeftTheConference;
};

export const buildAutomaticSurveyData = ({
    shouldShowResolutionMenu,
    resolutionStatus,
    isClaimEligible,
    claimFiled,
}: {
    shouldShowResolutionMenu: boolean;
    resolutionStatus: SummaryResolution;
    isClaimEligible: boolean;
    claimFiled: boolean;
}): Record<string, any> => {
    return {
        ...(shouldShowResolutionMenu && { resolution: resolutionStatus }),
        ...(isClaimEligible && { claimFiled }),
    };
};
